import type { Product } from '@scayle/storefront-nuxt'

const globalAvailabilityState = {
  availabilityMap: ref<Map<number, boolean>>(new Map()),
  isCheckingAvailability: ref(false),
}

export function useVTOBatchAvailability() {
  const checkFrameAvailability = useRpcCall('checkFrameAvailability')

  const checkBatchAvailability = async (products: Product[]) => {
    if (
      !products.length ||
      globalAvailabilityState.isCheckingAvailability.value
    ) {
      return
    }

    globalAvailabilityState.isCheckingAvailability.value = true

    const references: { productId: number; reference: string }[] = []

    products.forEach((product) => {
      const variants = product.variants || []
      variants.forEach((variant) => {
        const reference = getVirtualTryOnEan(variant)
        if (reference) {
          references.push({ productId: product.id, reference })
        }
      })
    })

    if (!references.length) {
      globalAvailabilityState.isCheckingAvailability.value = false
      return
    }

    const uniqueReferences = [...new Set(references.map((r) => r.reference))]

    try {
      const availabilityData = await checkFrameAvailability(uniqueReferences)

      if (availabilityData) {
        const referenceAvailability = new Map(
          availabilityData.map((item: { uid: string; available: boolean }) => [
            item.uid,
            item.available,
          ]),
        )

        references.forEach(({ productId, reference }) => {
          const isAvailable = referenceAvailability.get(reference) || false
          globalAvailabilityState.availabilityMap.value.set(
            productId,
            isAvailable,
          )
        })
      }
    } catch (error) {
      console.error(
        '[VTOBatchAvailability] Error checking availability:',
        error,
      )
      references.forEach(({ productId }) => {
        globalAvailabilityState.availabilityMap.value.set(productId, false)
      })
    } finally {
      globalAvailabilityState.isCheckingAvailability.value = false
    }
  }

  const isProductVTOAvailable = (productId: number) => {
    return globalAvailabilityState.availabilityMap.value.get(productId) || false
  }

  return {
    isCheckingAvailability: readonly(
      globalAvailabilityState.isCheckingAvailability,
    ),
    checkBatchAvailability,
    isProductVTOAvailable,
    availabilityMap: readonly(globalAvailabilityState.availabilityMap),
  }
}
